import { useEffect, useRef } from 'react';
import type { z } from 'zod';
import { useTableFilterPreferences } from './useTableFilterPreferences';

/** Trailing debounce for persisted-filter saves (see recordFilterChange). */
const SAVE_DEBOUNCE_MS = 600;

/**
 * URL-driven list pages + per-user saved filters, packaged: remembers the
 * last-used filter/sort/pageSize values through `useTableFilterPreferences`
 * and seeds them back into the URL on a fresh visit. Extracted from the
 * collection-threads table so the AR list pages (payments, remittances, cash
 * receipts, …) share one implementation of the seed/save dance.
 *
 * The URL stays the live source of truth; this hook only
 *  - seeds it once per mount, after the saved row has loaded, and only when
 *    the URL carries none of the persisted keys (a deep link with explicit
 *    filters wins), and
 *  - records changes that touch a persisted key, skipping no-op writes.
 *
 * Seed resolution mirrors the collections table:
 *  - saved row present & non-empty → restore the user's last-used values;
 *  - saved row present but empty ({}) → the user deliberately cleared their
 *    filters; honor that and seed nothing;
 *  - no saved row at all → brand-new user; seed `defaults` (when given).
 */
export function useSeededTableFilterPrefs<
  TPrefs extends Record<string, unknown>,
>({
  tableId,
  schema,
  persistedKeys,
  searchParams,
  defaults,
  onSeed,
}: {
  /** Stable key for the saved-filter row in `core_user_table_filter_configs`. */
  tableId: string;
  /** Module-scope Zod schema describing the persisted subset of the params. */
  schema: z.ZodType<TPrefs>;
  /**
   * The search-param keys to remember, in a fixed order (the order makes
   * `JSON.stringify` a reliable equality check for skipping no-op saves).
   */
  persistedKeys: readonly (string & keyof TPrefs)[];
  /** The route's current search params (a superset of TPrefs). */
  searchParams: Record<string, unknown>;
  /** Seed for a brand-new user who has no saved row yet. */
  defaults?: TPrefs;
  /** Apply a seed to the URL — typically `navigate({ search: …, replace: true })`. */
  onSeed: (seed: TPrefs) => void;
}) {
  const { savedFilters, saveFilters, isPending } = useTableFilterPreferences(
    tableId,
    schema,
  );

  // Project params down to the persisted shape: fixed key order, empty values
  // (undefined/null/''/[]) dropped so stored rows stay minimal and comparable.
  // A schema rejection returns null — never {} — so a validation failure can't
  // masquerade as "the user cleared their filters" and overwrite the saved row.
  const buildPrefs = (params: Record<string, unknown>): TPrefs | null => {
    const picked: Record<string, unknown> = {};
    for (const key of persistedKeys) {
      const value = params[key];
      if (value === undefined || value === null || value === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      picked[key] = value;
    }
    const parsed = schema.safeParse(picked);
    return parsed.success ? parsed.data : null;
  };

  // One-shot seed per mount; the ref also keeps a later "clear filters"
  // (which empties the URL) from re-applying the old saved row. The effect
  // deliberately has no dependency array: it re-runs until the saved row
  // loads, and always closes over the current onSeed — no latest-ref needed.
  const didSeedRef = useRef(false);
  useEffect(() => {
    if (didSeedRef.current || isPending) return;
    didSeedRef.current = true;
    const urlHasFilters = persistedKeys.some(
      (key) => searchParams[key] !== undefined,
    );
    if (urlHasFilters) return;
    const seed = savedFilters ?? defaults;
    if (!seed) return;
    const pruned = buildPrefs(seed);
    if (!pruned || Object.keys(pruned).length === 0) return;
    onSeed(pruned);
  });

  // Saves are debounced (trailing, latest-wins) so typing in a search box
  // doesn't send one upsert per keystroke. Keeping at most one timer live
  // also means saves from this hook can't leapfrog each other and land a
  // stale value. The pending save flushes on unmount so a quick navigation
  // doesn't lose the last change.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<(() => void) | null>(null);
  const cancelPendingSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    pendingSaveRef.current = null;
  };
  useEffect(() => {
    return () => {
      const flush = pendingSaveRef.current;
      cancelPendingSave();
      flush?.();
    };
  }, []);

  /**
   * Call from the page's `updateSearchParams` with the update patch and the
   * merged next params; persists only when the patch touches a persisted key
   * and the resulting row actually differs from what's saved.
   */
  const recordFilterChange = (
    updates: Record<string, unknown>,
    merged: Record<string, unknown>,
  ) => {
    if (!persistedKeys.some((key) => key in updates)) return;
    const next = buildPrefs(merged);
    if (next === null) return;
    const prev = buildPrefs(savedFilters ?? {}) ?? {};
    // Cancel any pending save even when the value is back to what's stored —
    // otherwise a queued intermediate value (e.g. a half-typed search that was
    // then deleted) would still fire and overwrite the current state.
    cancelPendingSave();
    if (JSON.stringify(next) === JSON.stringify(prev)) return;
    pendingSaveRef.current = () => saveFilters(next);
    saveTimerRef.current = setTimeout(() => {
      const flush = pendingSaveRef.current;
      cancelPendingSave();
      flush?.();
    }, SAVE_DEBOUNCE_MS);
  };

  return { savedFilters, recordFilterChange };
}
